import React, { useState, useMemo } from "react";
import Layout from "../../components/ui/Layout/Layout";
import Pageheader from "../../components/ui/PageHeader/Pageheader";
import TotalSalesStats from "../../components/sales/TotalSalesStats";
import "./salesbookPage.css";
import SalesTable from "../../components/sales/salesTable";
import ConfirmCard from "../../components/ui/ConfirmCard/ConfirmCard";
import Snackbar from "../../components/ui/Snackbar/Snackbar";
import SalesViewModal from "../../components/sales/SalesViewModal";
import TopSalesCard from "../../components/sales/TopSales";
import RevenueStats from "../../components/sales/RevenueStats";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { useSales } from "../../hooks/useSales";

const DATE_FILTERS = [
  { value: "today", label: "Today" },
  { value: "yesterday", label: "Yesterday" },
  { value: "week", label: "This Week" },
  { value: "month", label: "This Month" },
  { value: "all", label: "All Time" },
  { value: "custom", label: "Custom Date" },
];

// Filter using backend date directly
const filterSalesByDate = (sales, filter, customDate) => {
  const todayStr = new Date().toISOString().slice(0, 10);
  const yesterday = new Date();
  yesterday.setDate(yesterday.getDate() - 1);
  const yesterdayStr = yesterday.toISOString().slice(0, 10);

  switch (filter) {
    case "today":
      return sales.filter(s => s.createdAt.slice(0, 10) === todayStr);
    case "yesterday":
      return sales.filter(s => s.createdAt.slice(0, 10) === yesterdayStr);
    case "week": {
      const weekAgo = new Date();
      weekAgo.setDate(weekAgo.getDate() - 7);
      const weekAgoStr = weekAgo.toISOString().slice(0, 10);
      return sales.filter(s => s.createdAt.slice(0, 10) >= weekAgoStr);
    }
    case "month": {
      const month = todayStr.slice(0, 7);
      return sales.filter(s => s.createdAt.startsWith(month));
    }
    case "custom":
      return customDate
        ? sales.filter(s => s.createdAt.slice(0, 10) === customDate)
        : sales;
    default:
      return sales;
  }
};

const SalesBookPage = () => {
  const [viewSale, setViewSale] = useState(null);
  const [deleteSale, setDeleteSale] = useState(null);
  const [showView, setShowView] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [snackbar, setSnackbar] = useState(null);

  const [dateFilter, setDateFilter] = useState("today");
  const [customDate, setCustomDate] = useState("");

  const { sales, loading, deleteSaleById } = useSales({ sort: "newest" });

  const filteredSales = useMemo(
    () => filterSalesByDate(sales, dateFilter, customDate),
    [sales, dateFilter, customDate]
  );

  const handleView = sale => { setViewSale(sale); setShowView(true); };
  const handleDelete = sale => { setDeleteSale(sale); setShowConfirm(true); };

  const confirmDelete = async () => {
    if (!deleteSale) return;
    try {
      const res = await deleteSaleById(deleteSale._id);
      setSnackbar({ message: res.message, type: res.type });
    } catch {
      setSnackbar({ message: "Delete failed", type: "error" });
    } finally {
      setShowConfirm(false);
      setDeleteSale(null);
    }
  };

  const handlePrintTable = () => {
    const filterLabel = DATE_FILTERS.find(f => f.value === dateFilter)?.label || "";
    const rows = filteredSales.map(sale =>
      `<tr>
        <td>#${sale._id.slice(-6).toUpperCase()}</td>
        <td>${sale.walkInCustomer || "Walk-in"}</td>
        <td>${sale.createdAt.slice(0,10)}</td>
        <td>${sale.items.length} item${sale.items.length !== 1 ? "s" : ""}</td>
        <td>₹${sale.total?.toLocaleString("en-IN")}</td>
        <td>${sale.paymentType || "—"}</td>
      </tr>`
    ).join("");

    const totalRevenue = filteredSales.reduce((s, x) => s + (x.total || 0), 0);

    const printContent = `
      <html><head><title>Sales Report — ${filterLabel}</title>
      <style>
        body { font-family: Arial, sans-serif; font-size: 12px; padding: 20px; }
        h2 { margin-bottom: 4px; }
        p { margin: 0 0 12px; color: #666; }
        table { width: 100%; border-collapse: collapse; }
        th, td { border: 1px solid #ddd; padding: 6px 8px; text-align: left; }
        th { background: #f5f5f5; font-weight: 600; }
        .total-row td { font-weight: bold; background: #f9f9f9; }
      </style></head>
      <body>
        <h2>Sales Report</h2>
        <p>Period: ${filterLabel}${dateFilter === "custom" && customDate ? " — " + customDate : ""} &nbsp;|&nbsp; Total: ${filteredSales.length} sale${filteredSales.length !== 1 ? "s" : ""}</p>
        <table>
          <thead><tr><th>#ID</th><th>Customer</th><th>Date</th><th>Items</th><th>Amount</th><th>Payment</th></tr></thead>
          <tbody>${rows}</tbody>
          <tfoot><tr class="total-row"><td colspan="4">Total Revenue</td><td colspan="2">₹${totalRevenue.toLocaleString("en-IN")}</td></tr></tfoot>
        </table>
      </body></html>
    `;
    const w = window.open("", "_blank");
    w.document.write(printContent);
    w.document.close();
    w.print();
  };

  return (
    <Layout>
      <Pageheader title="Sales Management" showBtn={false} />

      <div className="stats-card">
        <RevenueStats variant="daily" />
        <TotalSalesStats variant="daily" />
      </div>

      <div className="sales-filter-bar">
        <div className="date-filter-tabs">
          {DATE_FILTERS.filter(f => f.value !== "custom").map(f => (
            <button
              key={f.value}
              className={`date-tab ${dateFilter === f.value ? "active" : ""}`}
              onClick={() => { setDateFilter(f.value); setCustomDate(""); }}
            >
              {f.label}
            </button>
          ))}
          <button
            className={`date-tab ${dateFilter === "custom" ? "active" : ""}`}
            onClick={() => setDateFilter("custom")}
          >
            <FontAwesomeIcon icon="calendar" /> Pick Date
          </button>
        </div>

        <div className="sales-filter-right">
          {dateFilter === "custom" && (
            <input
              type="date"
              className="date-picker-input"
              value={customDate}
              onChange={(e) => setCustomDate(e.target.value)}
              max={new Date().toISOString().slice(0,10)}
            />
          )}
          <button className="print-table-btn" onClick={handlePrintTable}>
            <FontAwesomeIcon icon="print" /> Print Report
          </button>
        </div>
      </div>

      <div className="sales-content-wrapper">
        <div className="sales-left">
          <SalesTable
            data={filteredSales}
            loading={loading}
            editable
            onView={handleView}
            onDelete={handleDelete}
            showViewAll={false}
          />
        </div>
        <div className="sales-right">
          <TopSalesCard sales={sales} limit={5} />
        </div>
      </div>

      {showView && <SalesViewModal sale={viewSale} onClose={() => setShowView(false)} />}
      {showConfirm && <ConfirmCard closeWindow={() => setShowConfirm(false)} onConfirm={confirmDelete} />}
      {snackbar && <Snackbar message={snackbar.message} type={snackbar.type} onClose={() => setSnackbar(null)} />}
    </Layout>
  );
};

export default SalesBookPage;